import * as React from 'react'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material'
import { useNotify } from 'react-admin'

import { resetPassword } from '../dataProvider'
import { handleAdminError } from '../utils/errorHandler'
import { Logo } from '../theme/Logo'
import { validatePasswordRules } from './resetPasswordValidation'

const ResetPassword = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const navigate = useNavigate()
  const notify = useNotify()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [loading, setLoading] = useState(false)

  const newPasswordRuleError = password.length > 0 ? validatePasswordRules(password) : null
  const passwordsMismatch =
    password.length > 0 && confirmPassword.length > 0 && password !== confirmPassword
  const canSubmit =
    !loading &&
    password.length > 0 &&
    confirmPassword.length > 0 &&
    newPasswordRuleError === null &&
    !passwordsMismatch

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!canSubmit) {
      return
    }
    const pErr = validatePasswordRules(password)
    setPasswordError(pErr || '')
    if (pErr) {
      return
    }
    setLoading(true)
    try {
      await resetPassword({ token, newPassword: password })
      notify('Your password was updated. You can sign in with your new password.', {
        type: 'success',
      })
      navigate('/login')
    } catch (error) {
      notify(handleAdminError(error), { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background:
            'radial-gradient(circle at 50% 14em, #313264 0%, #00023b 60%, #00023b 100%)',
          p: 2,
        }}
      >
        <Card sx={{ minWidth: 320, maxWidth: 400 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Logo />
            </Box>
            <Typography color="error" gutterBottom>
              This reset link is invalid or expired.
            </Typography>
            <Button variant="text" onClick={() => navigate('/login')}>
              Back to sign in
            </Button>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at 50% 14em, #313264 0%, #00023b 60%, #00023b 100%)',
        p: 2,
      }}
    >
      <Card sx={{ minWidth: 320, maxWidth: 400 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Logo />
          </Box>
          <Typography variant="h6" gutterBottom align="center">
            Set new password
          </Typography>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              margin="normal"
              label="New password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setPasswordError('')
              }}
              error={Boolean(passwordError || newPasswordRuleError)}
              helperText={passwordError || newPasswordRuleError || ''}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value)
              }}
              error={passwordsMismatch}
              helperText={
                passwordsMismatch
                  ? 'Passwords do not match'
                  : password.length > 0 &&
                    confirmPassword.length > 0 &&
                    password === confirmPassword
                  ? 'Passwords match'
                  : ''
              }
              FormHelperTextProps={
                passwordsMismatch
                  ? {}
                  : password.length > 0 &&
                    confirmPassword.length > 0 &&
                    password === confirmPassword
                  ? { sx: { color: 'success.main' } }
                  : {}
              }
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={!canSubmit}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={22} thickness={3} /> : 'Reset password'}
            </Button>
          </Box>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button variant="text" onClick={() => navigate('/login')}>
              Back to sign in
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export default ResetPassword
